import { z } from "zod";

// Common field validations
const emailSchema = z
  .string()
  .trim()
  .min(1, { message: "Email is required" })
  .email({ message: "Invalid email address" })
  .max(255, { message: "Email must be less than 255 characters" });

const nameSchema = z
  .string()
  .trim()
  .min(1, { message: "Name is required" })
  .max(100, { message: "Name must be less than 100 characters" });

const phoneSchema = z
  .string()
  .trim()
  .regex(/^[0-9+\-\s()]*$/, { message: "Invalid phone format" })
  .max(20, { message: "Phone must be less than 20 characters" })
  .optional()
  .or(z.literal(""));

const companyNameSchema = z
  .string()
  .trim()
  .min(1, { message: "Company name is required" })
  .max(200, { message: "Company name must be less than 200 characters" });

const messageSchema = z
  .string()
  .trim()
  .max(1000, { message: "Message must be less than 1000 characters" })
  .optional()
  .or(z.literal(""));

// Password schema for registration
export const passwordSchema = z
  .string()
  .min(8, { message: "Password must be at least 8 characters" })
  .max(72, { message: "Password must be less than 72 characters" });

// Registration form schema (with password for auto-approval flow)
export const registrationSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  confirmPassword: z.string().min(1, { message: "Please confirm your password" }),
  fullName: nameSchema,
  phone: phoneSchema,
  companyName: companyNameSchema,
  businessType: z.string().min(1, { message: "Business type is required" }),
  referralSource: z.string().optional().or(z.literal("")),
  message: messageSchema,
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

export type RegistrationFormData = z.infer<typeof registrationSchema>;

// Manual order form schema
export const manualOrderSchema = z.object({
  orderNumber: z.string().trim().max(50, { message: "Order number must be less than 50 characters" }).optional().or(z.literal("")),
  customerName: nameSchema,
  customerPhone: phoneSchema,
  address: z.string().trim().max(500, { message: "Address must be less than 500 characters" }).optional().or(z.literal("")),
  message: z.string().trim().max(2000, { message: "Message must be less than 2000 characters" }).optional().or(z.literal("")),
  source: z.string().min(1, { message: "Source is required" }),
  category: z.string().min(1, { message: "Category is required" }),
  totalPrice: z
    .string()
    .refine((val) => val === "" || !isNaN(parseFloat(val)), { message: "Invalid price" })
    .refine((val) => val === "" || parseFloat(val) >= 0, { message: "Price cannot be negative" })
    .optional()
    .or(z.literal("")),
  comments: z.string().trim().max(1000, { message: "Comments must be less than 1000 characters" }).optional().or(z.literal("")),
  csrName: z.string().trim().max(100, { message: "CSR name must be less than 100 characters" }).optional().or(z.literal("")),
});

export type ManualOrderFormData = z.infer<typeof manualOrderSchema>;

// Login form schema
export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, { message: "Password is required" }),
});

export type LoginFormData = z.infer<typeof loginSchema>;

// Password reset schema
export const passwordResetSchema = z.object({
  email: emailSchema,
});

export type PasswordResetFormData = z.infer<typeof passwordResetSchema>;

// Order detail edit schema
export const orderDetailSchema = z.object({
  orderNumber: z.string().trim().max(50, { message: "Order number must be less than 50 characters" }).optional().or(z.literal("")),
  address: z.string().trim().max(500, { message: "Address must be less than 500 characters" }).optional().or(z.literal("")),
  totalPrice: z
    .string()
    .refine((val) => val === "" || !isNaN(parseFloat(val)), { message: "Price must be a valid number" })
    .refine((val) => val === "" || parseFloat(val) >= 0, { message: "Price cannot be negative" })
    .refine((val) => val === "" || parseFloat(val) <= 1000000, { message: "Price cannot exceed 1,000,000" })
    .optional()
    .or(z.literal("")),
  comments: z.string().trim().max(2000, { message: "Comments must be less than 2000 characters" }).optional().or(z.literal("")),
  assignedTo: z.string().trim().max(100, { message: "Assigned person name must be less than 100 characters" }).optional().or(z.literal("")),
  parsedItems: z.string().trim().max(10000, { message: "Parsed items must be less than 10000 characters" }).optional().or(z.literal("")),
});

export type OrderDetailFormData = z.infer<typeof orderDetailSchema>;
